"use client";

import { AlertCircle, CheckCircle, FileSpreadsheet, X } from "lucide-react";
import React, { useState } from "react";
import * as XLSX from "xlsx";
import { apiClient } from "../../../lib/apiClient";

interface ExcelImportResult {
  importadas: number;
  errores: Array<{
    fila: number;
    error: string;
  }>;
  preguntasImportadas: Array<{
    id: number;
    numero: number;
    contenido: string;
  }>;
}

interface ImportarExcelModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportSuccess: () => void;
}

export default function ImportarExcelModal({
  isOpen,
  onClose,
  onImportSuccess,
}: ImportarExcelModalProps) {
  const [excelFile, setExcelFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [excelResult, setExcelResult] = useState<ExcelImportResult | null>(
    null
  );
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [validationResults, setValidationResults] = useState<{
    isValid: boolean;
    totalRows: number;
    validRows: number;
    errors: Array<{
      fila: number;
      error: string;
    }>;
    sampleData?: any[];
  } | null>(null);

  const validateExcelFile = async (file: File) => {
    setValidating(true);
    setMessage(null);
    setValidationResults(null);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      // Normalizar las cabeceras a minúsculas
      const normalizedData = jsonData.map((row: any) => {
        const normalizedRow: any = {};
        Object.keys(row).forEach((key) => {
          const normalizedKey = key.toLowerCase().trim();
          normalizedRow[normalizedKey] = row[key];
        });
        return normalizedRow;
      });

      if (!normalizedData || normalizedData.length === 0) {
        setValidationResults({
          isValid: false,
          totalRows: 0,
          validRows: 0,
          errors: [
            {
              fila: 0,
              error: "El archivo Excel está vacío o no contiene datos válidos",
            },
          ],
        });
        setValidating(false);
        return;
      }

      const errors: Array<{ fila: number; error: string }> = [];
      let validRows = 0;

      normalizedData.forEach((row: any, index: number) => {
        const fila = index + 2;
        const rowErrors: string[] = [];

        // Validar campos requeridos (ahora todos en minúsculas)
        if (!row.contenido) rowErrors.push("Falta campo 'contenido'");
        if (!row.respuesta) rowErrors.push("Falta campo 'respuesta'");
        if (!row.explicacion) rowErrors.push("Falta campo 'explicacion'");
        // anio es opcional - si no está, se usará el año actual
        if (!row.numero) rowErrors.push("Falta campo 'numero'");
        if (!row.asignatura) rowErrors.push("Falta campo 'asignatura'");

        // Validar tipos de datos
        if (row.anio && isNaN(parseInt(row.anio))) {
          rowErrors.push("Campo 'anio' debe ser un número");
        }
        if (row.numero && isNaN(parseInt(row.numero))) {
          rowErrors.push("Campo 'numero' debe ser un número");
        }
        if (row.asignatura && isNaN(parseInt(row.asignatura))) {
          rowErrors.push("Campo 'asignatura' debe ser un número");
        }

        // Validar respuesta
        if (
          row.respuesta &&
          !["A", "B", "C", "D"].includes(row.respuesta.toUpperCase())
        ) {
          rowErrors.push("Campo 'respuesta' debe ser A, B, C o D");
        }

        // Validar alternativas si existen
        if (row.alternativas) {
          try {
            let alternativas;

            if (typeof row.alternativas === "string") {
              // Pre-procesar el string para manejar caracteres especiales de LaTeX
              let processedString = row.alternativas;

              // Reemplazar secuencias problemáticas antes del parse
              processedString = processedString
                .replace(/\\,/g, "\\\\,") // Escapar \, para que sea \\,
                .replace(/\\pi/g, "\\\\pi") // Escapar \pi para que sea \\pi
                .replace(
                  /\\[a-zA-Z]/g,
                  (match: string) => "\\\\" + match.substring(1)
                ); // Escapar otros comandos LaTeX

              alternativas = JSON.parse(processedString);
            } else {
              alternativas = row.alternativas;
            }

            if (typeof alternativas !== "object" || alternativas === null) {
              rowErrors.push(
                "Campo 'alternativas' debe ser un objeto JSON válido"
              );
            } else {
              // Verificar que tenga las claves A, B, C, D
              const keys = Object.keys(alternativas);
              if (
                !keys.includes("A") ||
                !keys.includes("B") ||
                !keys.includes("C") ||
                !keys.includes("D")
              ) {
                rowErrors.push(
                  "Campo 'alternativas' debe contener las opciones A, B, C y D"
                );
              }
            }
          } catch (parseError) {
            // Intentar parseo alternativo para casos con caracteres especiales
            try {
              if (typeof row.alternativas === "string") {
                // Método alternativo: usar eval (más permisivo pero menos seguro)
                // Solo para casos donde JSON.parse falla
                const cleanedString = row.alternativas
                  .replace(/\\,/g, "\\\\,")
                  .replace(/\\pi/g, "\\\\pi");

                const alternativas = JSON.parse(cleanedString);

                if (typeof alternativas === "object" && alternativas !== null) {
                  const keys = Object.keys(alternativas);
                  if (
                    keys.includes("A") &&
                    keys.includes("B") &&
                    keys.includes("C") &&
                    keys.includes("D")
                  ) {
                    // Si llegamos aquí, el parseo alternativo funcionó
                    // No agregar error
                  } else {
                    rowErrors.push(
                      "Campo 'alternativas' debe contener las opciones A, B, C y D"
                    );
                  }
                } else {
                  rowErrors.push(
                    "Campo 'alternativas' tiene formato JSON inválido"
                  );
                }
              } else {
                rowErrors.push(
                  "Campo 'alternativas' tiene formato JSON inválido"
                );
              }
            } catch (secondError) {
              rowErrors.push(
                "Campo 'alternativas' tiene formato JSON inválido"
              );
            }
          }
        }

        // Validar dificultad si existe
        if (
          row.dificultad &&
          (isNaN(parseInt(row.dificultad)) ||
            parseInt(row.dificultad) < 1 ||
            parseInt(row.dificultad) > 5)
        ) {
          rowErrors.push("Campo 'dificultad' debe ser un número entre 1 y 5");
        }

        // Validar tiempo estimado si existe
        if (
          row.tiempoestimado &&
          (isNaN(parseInt(row.tiempoestimado)) ||
            parseInt(row.tiempoestimado) < 30 ||
            parseInt(row.tiempoestimado) > 300)
        ) {
          rowErrors.push(
            "Campo 'tiempoEstimado' debe ser un número entre 30 y 300 segundos"
          );
        }

        if (rowErrors.length > 0) {
          errors.push({
            fila,
            error: rowErrors.join(", "),
          });
        } else {
          validRows++;
        }
      });

      const isValid = errors.length === 0;

      setValidationResults({
        isValid,
        totalRows: normalizedData.length,
        validRows,
        errors,
        sampleData: normalizedData.slice(0, 3),
      });

      if (isValid) {
        setMessage({
          type: "success",
          text: `✅ Archivo válido: ${validRows} preguntas listas para importar`,
        });
      } else {
        setMessage({
          type: "error",
          text: `❌ Se encontraron ${errors.length} errores en ${normalizedData.length} filas`,
        });
      }
    } catch (error) {
      setValidationResults({
        isValid: false,
        totalRows: 0,
        validRows: 0,
        errors: [
          {
            fila: 0,
            error:
              "Error al leer el archivo Excel. Verifica que sea un archivo válido.",
          },
        ],
      });
      setMessage({
        type: "error",
        text: "Error al procesar el archivo Excel",
      });
    } finally {
      setValidating(false);
    }
  };

  const handleExcelUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!excelFile) {
      setMessage({
        type: "error",
        text: "Por favor selecciona un archivo Excel",
      });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const formData = new FormData();
      formData.append("archivo", excelFile);

      const response = await apiClient.post(
        "/simulaciones/preguntas-paes-excel",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const result = response.data;

      setExcelResult(result);
      setMessage({
        type: "success",
        text: `Importación completada: ${result.importadas} preguntas importadas`,
      });
      onImportSuccess();
    } catch (error: any) {
      console.error("Error al importar preguntas:", error);
      setMessage({
        type: "error",
        text: error.response?.data?.message || "Error de conexión",
      });
    } finally {
      setLoading(false);
    }
  };

  const descargarPlantilla = () => {
    // Crear datos de ejemplo para la plantilla
    const datosEjemplo = [
      {
        contenido: "Si $x + 5 = 12$, ¿cuál es el valor de $x$?",
        respuesta: "C",
        explicacion:
          "Resolviendo la ecuación: $x + 5 = 12$, entonces $x = 12 - 5 = 7$",
        anio: 2024,
        periodo: "Regular",
        numero: 1,
        asignatura: 1,
        alternativas: '{"A": "5", "B": "6", "C": "7", "D": "8"}',
        dificultad: 3,
        tiempoestimado: 60,
        tags: "algebra,ecuaciones",
        fuente: "PAES 2024",
        creadopor: "Sistema",
        tiposimulacion: "M1",
      },
      {
        contenido:
          "¿Cuál es el área de un triángulo con base de 8 cm y altura de 6 cm?",
        respuesta: "B",
        explicacion:
          "El área de un triángulo es $\\frac{base \\times altura}{2} = \\frac{8 \\times 6}{2} = 24$ cm²",
        anio: 2024,
        periodo: "Regular",
        numero: 2,
        asignatura: 1,
        alternativas:
          '{"A": "14 cm²", "B": "24 cm²", "C": "28 cm²", "D": "48 cm²"}',
        dificultad: 2,
        tiempoestimado: 45,
        tags: "geometria,area",
        fuente: "PAES 2024",
        creadopor: "Sistema",
        tiposimulacion: "M1",
      },
      {
        contenido: "¿Cuál es el máximo común divisor (MCD) de 18 y 24?",
        respuesta: "C",
        explicacion:
          "Los divisores de 18 son: 1, 2, 3, 6, 9, 18. Los divisores de 24 son: 1, 2, 3, 4, 6, 8, 12, 24. El MCD es 6.",
        anio: 2024,
        periodo: "Regular",
        numero: 3,
        asignatura: 1,
        alternativas: '{"A": "3", "B": "4", "C": "6", "D": "8"}',
        dificultad: 2,
        tiempoestimado: 50,
        tags: "numeros,mcd",
        fuente: "PAES 2024",
        creadopor: "Sistema",
        tiposimulacion: "M1",
      },
    ];

    // Crear workbook
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(datosEjemplo);

    // Ajustar ancho de columnas
    const columnWidths = [
      { wch: 50 }, // contenido
      { wch: 10 }, // respuesta
      { wch: 60 }, // explicacion
      { wch: 8 }, // anio
      { wch: 12 }, // periodo
      { wch: 8 }, // numero
      { wch: 12 }, // asignatura
      { wch: 40 }, // alternativas
      { wch: 12 }, // dificultad
      { wch: 15 }, // tiempoestimado
      { wch: 20 }, // tags
      { wch: 15 }, // fuente
      { wch: 15 }, // creadopor
      { wch: 15 }, // tiposimulacion
    ];
    worksheet["!cols"] = columnWidths;

    XLSX.utils.book_append_sheet(workbook, worksheet, "Preguntas");

    // Descargar el archivo
    XLSX.writeFile(workbook, "plantilla_preguntas_paes.xlsx");
  };

  const resetModal = () => {
    setExcelFile(null);
    setExcelResult(null);
    setMessage(null);
    setLoading(false);
    setValidationResults(null);
    setValidating(false);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <FileSpreadsheet className="h-6 w-6 text-orange-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              Importar desde Excel
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-6">
            <p className="text-gray-600 mb-3">
              Sube un archivo Excel con múltiples preguntas para importar
              masivamente
            </p>
            <button
              type="button"
              onClick={descargarPlantilla}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors"
            >
              <FileSpreadsheet className="w-4 h-4" />
              Descargar Plantilla Excel
            </button>
          </div>

          <form onSubmit={handleExcelUpload} className="space-y-6">
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Archivo Excel
              </label>

              {/* Botón de seleccionar archivo personalizado */}
              <div className="relative">
                <input
                  id="excelFile"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    setExcelFile(file);
                    if (file) {
                      validateExcelFile(file);
                    } else {
                      setValidationResults(null);
                      setMessage(null);
                    }
                  }}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                <label
                  htmlFor="excelFile"
                  className="flex items-center justify-center w-full px-4 py-3 text-sm font-medium text-gray-700 bg-white border-2 border-dashed border-gray-300 rounded-lg hover:border-orange-400 hover:bg-orange-50 transition-colors cursor-pointer"
                >
                  <FileSpreadsheet className="w-5 h-5 mr-2 text-gray-400" />
                  {excelFile
                    ? "Cambiar archivo Excel"
                    : "Seleccionar archivo Excel"}
                </label>
              </div>

              {excelFile && (
                <div className="flex items-center space-x-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <FileSpreadsheet className="w-5 h-5 text-blue-500" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-blue-800">
                      {excelFile.name}
                    </p>
                    <p className="text-xs text-blue-600">
                      {(excelFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setExcelFile(null);
                      setValidationResults(null);
                      setMessage(null);
                    }}
                    className="text-blue-600 hover:text-blue-800 transition-colors"
                    type="button"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-blue-800 mb-2">
                  Formato requerido del Excel:
                </h4>
                <div className="text-xs text-blue-700 space-y-1">
                  <p>
                    • <strong>contenido:</strong> Contenido de la pregunta
                  </p>
                  <p>
                    • <strong>respuesta:</strong> Respuesta correcta (A, B, C,
                    D)
                  </p>
                  <p>
                    • <strong>explicacion:</strong> Explicación de la respuesta
                  </p>
                  <p>
                    • <strong>anio:</strong> Año de la pregunta (opcional, se
                    usará año actual si no se especifica)
                  </p>
                  <p>
                    • <strong>periodo:</strong> Período (opcional)
                  </p>
                  <p>
                    • <strong>numero:</strong> Número de la pregunta
                  </p>
                  <p>
                    • <strong>asignatura:</strong> ID de la asignatura
                  </p>
                  <p>
                    • <strong>alternativas:</strong> JSON con opciones{" "}
                    {'{"A": "Opción A", "B": "Opción B"}'}
                  </p>
                  <p>
                    • <strong>tags:</strong> Tags separados por comas
                  </p>
                  <p>
                    • <strong>fuente:</strong> Fuente de la pregunta (opcional)
                  </p>
                  <p>
                    • <strong>creadopor:</strong> Nombre del creador de la
                    pregunta (opcional)
                  </p>
                  <p>
                    • <strong>tipoSimulacion:</strong> Tipo de simulación
                    (opcional)
                  </p>
                </div>
              </div>
            </div>

            {/* Validación en tiempo real */}
            {validating && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-blue-800">
                    Validando archivo Excel...
                  </span>
                </div>
              </div>
            )}

            {/* Resultados de validación */}
            {validationResults && (
              <div className="space-y-4">
                <div
                  className={`p-4 rounded-lg border ${
                    validationResults.isValid
                      ? "bg-green-50 border-green-200"
                      : "bg-red-50 border-red-200"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    {validationResults.isValid ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : (
                      <AlertCircle className="h-5 w-5 text-red-600" />
                    )}
                    <h4
                      className={`font-semibold ${
                        validationResults.isValid
                          ? "text-green-800"
                          : "text-red-800"
                      }`}
                    >
                      {validationResults.isValid
                        ? "Archivo Válido"
                        : "Archivo con Errores"}
                    </h4>
                  </div>

                  <div className="text-sm space-y-1">
                    <p
                      className={
                        validationResults.isValid
                          ? "text-green-700"
                          : "text-red-700"
                      }
                    >
                      📊 Total de filas: {validationResults.totalRows}
                    </p>
                    <p
                      className={
                        validationResults.isValid
                          ? "text-green-700"
                          : "text-red-700"
                      }
                    >
                      ✅ Filas válidas: {validationResults.validRows}
                    </p>
                    {!validationResults.isValid && (
                      <p className="text-red-700">
                        ❌ Errores encontrados:{" "}
                        {validationResults.errors.length}
                      </p>
                    )}
                  </div>
                </div>

                {/* Lista de errores */}
                {validationResults.errors.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h4 className="font-semibold text-red-800 mb-3">
                      Errores de validación:
                    </h4>
                    <div className="max-h-48 overflow-y-auto space-y-2">
                      {validationResults.errors.map((error, index) => (
                        <div
                          key={index}
                          className="bg-white border border-red-200 rounded p-2"
                        >
                          <p className="text-sm font-medium text-red-800">
                            Fila {error.fila}:
                          </p>
                          <p className="text-xs text-red-600">{error.error}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Vista previa de datos válidos */}
                {validationResults.isValid && validationResults.sampleData && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="font-semibold text-green-800 mb-3">
                      Vista previa (primeras 3 filas):
                    </h4>
                    <div className="bg-white border border-green-200 rounded p-3">
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left p-1">Fila</th>
                              <th className="text-left p-1">Contenido</th>
                              <th className="text-left p-1">Respuesta</th>
                              <th className="text-left p-1">Año</th>
                              <th className="text-left p-1">Número</th>
                            </tr>
                          </thead>
                          <tbody>
                            {validationResults.sampleData.map((row, index) => (
                              <tr key={index} className="border-b">
                                <td className="p-1">{index + 1}</td>
                                <td className="p-1 max-w-xs truncate">
                                  {row.contenido || "N/A"}
                                </td>
                                <td className="p-1">
                                  {row.respuesta || "N/A"}
                                </td>
                                <td className="p-1">{row.anio || "N/A"}</td>
                                <td className="p-1">{row.numero || "N/A"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Mensajes */}
            {message && (
              <div
                className={`p-4 rounded-md flex items-center gap-2 ${
                  message.type === "success"
                    ? "bg-green-50 border border-green-200 text-green-800"
                    : "bg-red-50 border border-red-200 text-red-800"
                }`}
              >
                {message.type === "success" ? (
                  <CheckCircle className="h-4 w-4" />
                ) : (
                  <AlertCircle className="h-4 w-4" />
                )}
                <span className="text-sm">{message.text}</span>
              </div>
            )}

            {/* Resultados */}
            {excelResult && (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 text-green-800 p-4 rounded-md">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    <span>
                      Importación completada: {excelResult.importadas} preguntas
                      importadas
                    </span>
                  </div>
                </div>

                {excelResult.errores.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-semibold text-red-600">
                      Errores encontrados:
                    </h4>
                    <div className="max-h-40 overflow-y-auto bg-red-50 border border-red-200 rounded-md p-3">
                      {excelResult.errores.map((error, index) => (
                        <div key={index} className="text-sm text-red-600">
                          Fila {error.fila}: {error.error}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Botones */}
            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-md font-medium transition-colors"
              >
                Cerrar
              </button>
              <button
                type="submit"
                disabled={loading || !excelFile || !validationResults?.isValid}
                className="flex-1 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-md font-medium transition-colors"
              >
                {loading ? "Importando..." : "Importar Preguntas"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
